
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { useState } from "react";

interface CancelBookingDialogProps {
  bookingId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCancel: () => void;
}

const CancelBookingDialog = ({
  bookingId,
  open,
  onOpenChange,
  onCancel,
}: CancelBookingDialogProps) => {
  const [cancelReason, setCancelReason] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { toast } = useToast();

  const handleCancelRequest = async () => {
    if (!cancelReason.trim()) {
      toast({
        title: "Error",
        description: "Please provide a reason for cancellation",
        variant: "destructive",
      });
      return;
    }

    setIsSubmitting(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('User not authenticated');

      const { data: existingRequests } = await supabase
        .from('cancellation_requests')
        .select('id, status')
        .eq('booking_id', bookingId)
        .eq('user_id', user.id);

      if (existingRequests && existingRequests.length > 0) {
        const pendingRequest = existingRequests.find(req => req.status === 'pending');
        if (pendingRequest) {
          throw new Error('A pending cancellation request already exists for this booking');
        }
      }

      const { error: insertError } = await supabase
        .from('cancellation_requests')
        .insert({
          booking_id: bookingId,
          reason: cancelReason,
          user_id: user.id,
          status: 'pending'
        });

      if (insertError) throw insertError;

      toast({
        title: "Success",
        description: "Your cancellation request has been submitted and is pending admin approval.",
      });
      
      onOpenChange(false);
      setCancelReason("");
      onCancel();
    } catch (error: any) {
      console.error('Error requesting cancellation:', error);
      toast({
        title: "Error",
        description: error.message || "Failed to submit cancellation request. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Request Booking Cancellation</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <p className="text-sm text-gray-600">
            Please provide a reason for cancellation. Your request will be reviewed by an administrator.
          </p>
          <Textarea
            placeholder="Enter reason for cancellation..."
            value={cancelReason}
            onChange={(e) => setCancelReason(e.target.value)}
            className="min-h-[100px]"
          />
        </div>
        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => {
              onOpenChange(false);
              setCancelReason("");
            }}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button
            onClick={handleCancelRequest}
            disabled={isSubmitting || !cancelReason.trim()}
          >
            Submit Request
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default CancelBookingDialog;
